import { MetricStripSkeleton } from "@/components/analytics/MetricStrip";
import {
  HeaderSkeleton,
  StatCardsSkeleton,
  TableSkeleton,
  ListSkeleton,
} from "@/components/common/skeletons";
import {
  pageSkeletonShapeFor,
  type PageSkeletonVariant,
} from "@/components/common/pageSkeletonVariant";

/**
 * Content-shaped skeleton for route-level loading.
 *
 * Used as the router `Suspense` fallback so a lazy page (or a hard refresh)
 * shows a page-shaped placeholder instead of a spinner.
 *
 * **A page that also has a data-loading skeleton must render this one**, not a
 * second hand-built variant. Inventory had its own, five stat cards wide where
 * this is four, so a refresh showed the router's skeleton, then a *differently
 * shaped* skeleton, and the transition read as two separate loading screens.
 * The `stats` prop exists so a page can match its own card count rather than
 * fork the component to change one number.
 *
 * **The figures row is chosen by route, not by the caller.** Both placeholders
 * on a reload — the router's and the page's own — call this component, and if
 * each picked its own shape they would disagree exactly when it is most
 * visible. `pageSkeletonVariantFor` is the single answer both get. Pass
 * `variant` only to override it.
 */
export function PageSkeleton({
  stats,
  variant,
  header,
}: {
  /** Overrides the route's figure count. Prefer fixing the route's entry. */
  stats?: number;
  /**
   * Draw the title-and-subtitle block. `PageState` passes `false` when it
   * renders the real header itself: with the default, a page spent its data
   * load showing its real heading with a skeleton heading stacked under it,
   * and every block below sat one header-height lower than the fallback the
   * router had just drawn, so the whole page jumped when the code arrived.
   */
  header?: boolean;
  /**
   * Overrides the route-derived shape. Rarely needed: a page whose figures do
   * not match its route's entry should usually fix the entry, so the router's
   * fallback agrees too.
   */
  variant?: PageSkeletonVariant;
}) {
  // `window.location` rather than `useLocation`, so a skeleton never requires
  // router context. It is rendered from error boundaries, from `PageState`, and
  // in tests that have no Router — `useLocation` threw in all three. React
  // Router drives `history`, so `window.location.pathname` is already the
  // destination by the time a fallback renders.
  //
  // The cost is that this does not re-render on a route change. That is fine
  // for a placeholder: it mounts, shows, and unmounts. A `MemoryRouter` (tests)
  // does not touch `window.location`, so a test that cares passes `variant`.
  const pathname =
    typeof window === "undefined" ? "" : window.location.pathname;
  const routeShape = pageSkeletonShapeFor(pathname);
  const shape = variant ?? routeShape.variant;
  const count = stats ?? routeShape.stats;
  const drawHeader = header ?? routeShape.header;

  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading page">
      {drawHeader && <HeaderSkeleton />}
      {/* `none` is the common case: most pages go straight from header to
          content, and drawing a figures row for them promises something that
          never arrives. */}
      {shape === "strip" ? (
        <MetricStripSkeleton count={count} />
      ) : shape === "cards" ? (
        <StatCardsSkeleton count={count} />
      ) : null}
      {shape === "list" ? <ListSkeleton /> : <TableSkeleton />}
    </div>
  );
}
